import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Input, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
interface Customer {
  id: string;
  mobile: number;
  is_active: number;
  amount: string;
  total_package_amount: string;
  currency: string;
  amount_spent: string;
  amount_without_currency: string;
  total_package_amount_without_currency: string;
  amount_spent_without_currency: string;
}

export default function CustomerReport() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Customer[]>([]);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const { currency } = useCurrency();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(filterText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [filterText]);


  const fetchData = async (page = 1, perPage = 10, sortField = sortColumn, sortDir = sortOrder, search = filterText) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}customer_detail/getall`, {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearchTerm.trim() !== "" ? debouncedSearchTerm : null,
          sort_by: sortField,
          sort_order: sortDir,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data: Customer[] = response.data.data;
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
      // setCurrency(response.data.currency || "");
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, debouncedSearchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, sortColumn, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, sortColumn, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "created_at";

    setSortColumn(sortField);
    setSortOrder(sortDirection);
    setCurrentPage(1);
    fetchData(1, perPage, sortField, sortDirection);
  };

  const fetchAllCustomerDataWithoutPagination = async (
    searchText = ""
  ): Promise<Customer[]> => {
    try {
      const response = await axios.get(
        `${baseUrl}customer_detail/getallnopagination`,
        {
          params: {
            search: debouncedSearchTerm.trim() !== "" ? debouncedSearchTerm : undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.data || [];
    } catch (error) {
      console.error(
        "Error fetching all customer data without pagination:",
        error
      );
      return []; // Return empty array on error
    }
  };

  const handleFullReset = () => {
    setFilterText("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  const customerTableColumns = [
    {
      name: <div style={{ whiteSpace: "normal" }}>S.No</div>,
      cell: (row: Customer, index: number) =>
        (currentPage - 1) * perPage + index + 1,
      width: "80px",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Customer Mobile</div>,
      selector: (row: Customer) => row.mobile || "N/A",
      sortable: true,
      sortField: "mobile",
    },
    {
      name: (
        <div style={{ whiteSpace: "normal" }}>
          Total Purchase Amount {currency ? `(${currency})` : ""}
        </div>
      ),
      sortable: true,
      sortField: "total_package_amount",
      cell: (row: Customer) => row.total_package_amount_without_currency ?? 0,
    },
    {
      name: (
        <div style={{ whiteSpace: "normal" }}>
          Amount Spent {currency ? `(${currency})` : ""}
        </div>
      ),
      sortable: true,
      sortField: "amount_spent",
      cell: (row: Customer) => row.amount_spent_without_currency ?? 0,
    },
    {
      name: (
        <div style={{ whiteSpace: "normal" }}>
          Current Balance {currency ? `(${currency})` : ""}
        </div>
      ),
      sortable: true,
      sortField: "amount",
      cell: (row: Customer) => row.amount_without_currency ?? 0,
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };


  const exportPDF = async () => {
    const allCustomerData = await fetchAllCustomerDataWithoutPagination(
      debouncedSearchTerm
    );
    const tableData = allCustomerData.map((row, index) => [
      index + 1,
      row.mobile || "N/A",
      `${row.total_package_amount_without_currency || "0"}`,
      `${row.amount_spent_without_currency || 0}`,
      `${row.amount_without_currency || "0"}`,
    ]);

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currencyMap[currency] || currency;

    exportToPDF({
      fileName: "Customer_Report",
      columns: [
        "S.No",
        "Customer Mobile",
        `Total Purchase ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
        `Amount Spent ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
        `Current Balance ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
      ],
      data: tableData,
      emptyDataMessage: "No customer data available for export.",
      autoTableOptions: {
        headStyles: { fillColor: [0, 105, 92] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { halign: "center" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
        },
      },
    });
  };

  const exportExcel = async () => {
    const allCustomerData = await fetchAllCustomerDataWithoutPagination(
      debouncedSearchTerm
    );
    if (!allCustomerData.length) {
      toast.warn("No customer data available for export.", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      return;
    }

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const excelCurrency = currencyMap[currency] || currency;
    const totalPurchaseHeader = `Total Purchase Amount ${excelCurrency ? `(${excelCurrency})` : ""}`;
    const amountSpentHeader = `Amount Spent ${excelCurrency ? `(${excelCurrency})` : ""}`;
    const currentBalanceHeader = `Current Balance ${excelCurrency ? `(${excelCurrency})` : ""}`;

    const tableData = allCustomerData.map((row, index) => ({
      "S.No": index + 1,
      "Customer Mobile": row.mobile || "N/A",
      [totalPurchaseHeader]: row.total_package_amount_without_currency || "0",
      [amountSpentHeader]: row.amount_spent_without_currency || "0",
      [currentBalanceHeader]: row.amount_without_currency || "0",
    }));

    excelExportUtils(tableData, "Customer_Report", "Customers Summary");
  };

  return (
    <>
      <div className="page-body">
        <Breadcrumbs
          pageTitle="Customer Report"
          parent="Customer"
          title="Customer Report"
        />
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              <Row className=" justify-content-between">
                {/* Left side: Excel & PDF buttons */}
                <Col xl={2}>
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={exportPDF}
                      className="d-flex justify-content-center  p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="PDF"
                    >
                      <FaFilePdf size={16} />
                    </Button>
                    <Button
                      color="secondary"
                      onClick={exportExcel}
                      className="d-flex justify-content-center  p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="EXCEL"
                    >
                      <FaFileExcel size={16} />
                    </Button>
                  </div>
                </Col>

                {/* Right side: Search box and buttons */}
                <Col xl={3}>
                  <div className="mb-3 d-flex align-items-center">
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              <div className="table-responsive custom-scrollbar">
                <DataTable
                  data={filteredItems}
                  columns={customerTableColumns}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                  pagination
                  paginationServer
                  paginationPerPage={perPage}
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  paginationDefaultPage={currentPage}
                  sortServer
                  onSort={handleSort}
                  responsive
                  progressPending={loading}
                  progressComponent={
                    <div className="p-5">
                      <Spinner color="primary" />
                    </div>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <ToastContainer />
      </div>
    </>
  );
}
